import React, { useEffect, useRef, useState } from "react";
import UtilityBar from "./UtilityBar";
import Form from "../Form";
import { ViewListingTypes } from "../../enums/ViewListingTypes";
import ConditionalRender from "../../reusable-components/ConditionalRender";
import TileViewTable from "./TileViewTable";
import {
  invoiceManagementConsts,
  refreshKey,
  showInvoiceNotification,
} from "./constants";
import ListViewTable from "./ListViewTable";
import InvoicePreviewTemplate from "../InvoicePreviewTemplate";
import { storageKeys } from "../../constants/storageKeys";
import RolesServices from "../../services/roles.services";
import { GenerationStatus } from "../../enums/InvoiceManagementEnums";
import InvoiceManagementServices from "../../services/invoiceManagement.service";
import { InvoiceProgressType } from "../../Types/invoiceManagementTypes";
import { message, notification } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

const InvoiceManagement = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const { state: locationState } = location;
  const ticketProcessingInvoiceId = locationState?.invoiceId;

  const { LIST, KANBAN } = ViewListingTypes;
  const refreshStatusRef = useRef<boolean>(false);
  const [generatingInvoices, setGeneratingInvoices] =
    useState<GenerationStatus>(GenerationStatus.IDLE);
  const [invoiceProgress, setInvoiceProgress] = useState<
    InvoiceProgressType | undefined
  >(undefined);
  const [activeView, setActiveView] = useState();
  const { FetchInvoiceGenerationStatus } = InvoiceManagementServices();
  const { FetchUserRoleDetails } = RolesServices();

  const {
    data: invoiceGenerationStatus,
    refetch: refreshInvoiceGenerationStatus,
  } = FetchInvoiceGenerationStatus();

  const userDetails = localStorage.getItem(storageKeys.LS_USER_DETAILS);
  const userDetailsJson = JSON.parse(userDetails);
  const userRoleInfo =
    userDetailsJson?.[invoiceManagementConsts.ROLE_DETAILS_KEY];
  const userRoleProfileInfo =
    userDetailsJson?.[invoiceManagementConsts.PROFILE_DETAILS_KEY];

  const { data: roleDetails } = FetchUserRoleDetails(userRoleInfo);

  const editAccess = !!userRoleProfileInfo
    ? roleDetails?.[0]?.otherData?.[4]?.multiPannel?.[2]?.selectedOptions?.includes(
        invoiceManagementConsts.EDIT_ROLE,
      )
    : true;

  const [selectedKeysTracker, setSelectedKeysTracker] = useState([]);
  const [selectAllTracker, setSelectAllTracker] = useState<boolean>(false);
  const [totalListItems, setTotalListItems] = useState<number>(0);
  const [unselectedKeysLength, setUnselectedKeysLength] = useState<number>(0);
  const [searchState, setSearchState] = useState("");
  const [dateRange, setDateRange] = useState({});
  const [filters, setFilters] = useState({});
  const [bulkUpdateKey, setBulkUpdateKey] = useState<number>(0);

  const handleViewToggle = (view: ViewListingTypes) => {
    setActiveView(view);
    window.location.hash = view;
    if (view === KANBAN) setDateRange({});
  };

  const handleTileSelect = (startDate: string, endDate: string) => {
    setDateRange({
      startDate: startDate,
      endDate: endDate,
    });
    handleViewToggle(ViewListingTypes.LIST);
  };

  const handleTrackSelectedKeys = (selectedKeys: []) => {
    setSelectedKeysTracker(selectedKeys);
  };

  const handleTrackSelectAll = (selectAllValue) => {
    setSelectAllTracker(selectAllValue);
  };

  const refetchAction = () => {
    refreshStatusRef.current = true;
    notification.destroy(invoiceManagementConsts.PROGRESS_NOTIF_KEY);
    refreshInvoiceGenerationStatus();
  };

  useEffect(() => {
    if (
      invoiceGenerationStatus?.status === GenerationStatus.IDLE &&
      generatingInvoices === GenerationStatus.IDLE
    ) {
      return;
    }
    if (
      invoiceGenerationStatus?.status === GenerationStatus.IDLE &&
      generatingInvoices === GenerationStatus.IN_PROGRESS
    ) {
      message.success(invoiceManagementConsts.GENERATION_SUCCESS);
      queryClient.invalidateQueries(refreshKey[activeView]);
    }
    if (
      invoiceGenerationStatus?.failedTasks ===
        invoiceGenerationStatus?.totalTasks &&
      invoiceGenerationStatus?.totalTasks > 0
    ) {
      message.error(invoiceManagementConsts.GENERATION_FAILED);
      queryClient.invalidateQueries(refreshKey[activeView]);
    }
    if (
      refreshStatusRef.current &&
      invoiceGenerationStatus?.status !== GenerationStatus.IDLE
    ) {
      refreshStatusRef.current = false;
      showInvoiceNotification(invoiceGenerationStatus, refetchAction);
    }
    setGeneratingInvoices(invoiceGenerationStatus?.status);
    setInvoiceProgress(invoiceGenerationStatus);
  }, [invoiceGenerationStatus]);

  useEffect(() => {
    if (ticketProcessingInvoiceId) {
      setActiveView(LIST);
      setFilters({ invoiceId: [ticketProcessingInvoiceId] });
    } else {
      setActiveView(KANBAN);
    }
  }, []);

  return (
    <>
      <Form>
        {() => {
          return (
            <div>
              <UtilityBar
                activeView={activeView}
                handleViewToggle={handleViewToggle}
                handleSearch={setSearchState}
                dateRanges={dateRange}
                handleSetDateRange={setDateRange}
                handleSetFilter={setFilters}
                currentFilter={filters}
                selectedKeyList={selectedKeysTracker}
                totalRecords={totalListItems}
                unselectedKeysLength={unselectedKeysLength}
                selectAllValue={selectAllTracker}
                editAccess={editAccess}
                setBulkUpdateKey={setBulkUpdateKey}
                invoiceGenerationStatus={generatingInvoices}
                generationData={invoiceGenerationStatus}
                refetchAction={refetchAction}
              />
              <div>
                <ConditionalRender visible={activeView === KANBAN}>
                  <TileViewTable
                    searchValue={searchState}
                    dateRange={dateRange}
                    handleTileSelect={handleTileSelect}
                    editAccess={editAccess}
                  />
                </ConditionalRender>
                <ConditionalRender visible={activeView === LIST}>
                  <ListViewTable
                    searchVal={searchState}
                    dateRange={dateRange}
                    filters={filters}
                    trackSelectedKeys={handleTrackSelectedKeys}
                    trackSelectAll={handleTrackSelectAll}
                    trackTotalRecord={setTotalListItems}
                    trackUnselectedKeys={setUnselectedKeysLength}
                    editAccess={editAccess}
                    bulkUpdateKey={bulkUpdateKey}
                  />
                </ConditionalRender>
              </div>
            </div>
          );
        }}
      </Form>
    </>
  );
};

export default InvoiceManagement;
